(() => {
  if (customElements.get('mm-purchase-dock')) return;

  class MilliesPurchaseDock extends HTMLElement {
    connectedCallback() {
      if (this.disconnect || this.closest('dialog')) return;
      this.productRoot = this.closest('[data-mm-product]');
      this.form = this.productRoot?.querySelector('.mm-product-form');
      this.sourceAdd = this.form?.querySelector('[data-mm-add]');
      this.sourceBuy = this.form?.querySelector('[data-mm-buy-now]');
      this.sourceActions = this.form?.querySelector('[data-mm-main-purchase-actions], .buyrow');
      this.sourceData = this.productRoot?.querySelector('[data-mm-product-data]');
      if (!this.form || !this.sourceAdd || !this.sourceBuy || !this.sourceActions || !this.sourceData) return;

      this.addButton = this.querySelector('[data-mm-dock-add]');
      this.buyButton = this.querySelector('[data-mm-dock-buy]');
      this.summary = this.querySelector('[data-mm-dock-summary]');
      this.total = this.querySelector('[data-mm-dock-total]');
      this.unit = this.querySelector('[data-mm-dock-unit]');
      this.editButton = this.querySelector('[data-mm-dock-edit]');
      this.currency = this.dataset.currency || document.body.dataset.mmCurrency || 'GBP';
      this.formatMoney = new Intl.NumberFormat(this.dataset.locale || 'en-GB', { style: 'currency', currency: this.currency });
      this.frame = 0;
      this.events = new AbortController();
      const signal = this.events.signal;
      const schedule = () => this.schedule();
      this.form.addEventListener('input', schedule, { signal });
      this.form.addEventListener('change', schedule, { signal });
      this.form.addEventListener('submit', schedule, { signal });
      document.addEventListener('mm:product:change', schedule, { signal });
      document.addEventListener('mm:dispatch:updated', schedule, { signal });
      document.addEventListener('mm:gel-overlays', schedule, { signal });
      document.addEventListener('toggle', schedule, { signal, capture: true });
      document.addEventListener('close', schedule, { signal, capture: true });
      document.addEventListener('focusin', schedule, { signal });
      document.addEventListener('focusout', schedule, { signal });
      window.addEventListener('scroll', schedule, { signal, passive: true });
      window.addEventListener('resize', schedule, { signal, passive: true });
      window.visualViewport?.addEventListener('resize', schedule, { signal, passive: true });
      this.addButton.addEventListener('click', () => this.submit(this.sourceAdd), { signal });
      this.buyButton.addEventListener('click', () => this.submit(this.sourceBuy), { signal });
      this.editButton.addEventListener('click', () => this.toggleOptions(), { signal });
      this.createOptions(signal);

      this.mutationObserver = new MutationObserver(schedule);
      this.mutationObserver.observe(this.form, {
        subtree: true, childList: true, characterData: true, attributes: true,
        attributeFilter: ['disabled', 'hidden', 'aria-disabled', 'aria-busy', 'value', 'checked']
      });
      this.mutationObserver.observe(this.sourceData, { childList: true, characterData: true, subtree: true });
      this.overlayObserver = new MutationObserver(schedule);
      this.overlayObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
      this.resizeObserver = new ResizeObserver(schedule);
      [this.sourceActions, document.querySelector('.header'), document.querySelector('.mm-dispatch-bar')]
        .filter(Boolean).forEach(element => this.resizeObserver.observe(element));
      if ('IntersectionObserver' in window) {
        this.intersectionObserver = new IntersectionObserver(schedule);
        this.intersectionObserver.observe(this.sourceActions);
        const footer = document.querySelector('[data-mm-site-footer]') || document.querySelector('footer, .footer');
        if (footer) this.intersectionObserver.observe(footer);
      }
      this.disconnect = () => {
        this.events.abort();
        this.mutationObserver.disconnect();
        this.overlayObserver.disconnect();
        this.resizeObserver.disconnect();
        this.intersectionObserver?.disconnect();
        cancelAnimationFrame(this.frame);
      };
      this.dataset.mmReady = 'true';
      this.update();
    }

    disconnectedCallback() {
      this.disconnect?.();
      this.disconnect = null;
    }

    schedule() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.update();
      });
    }

    createOptions(signal) {
      this.options = document.createElement('div');
      this.options.className = 'mm-purchase-dock__options';
      this.options.id = `mm-dock-options-${this.productRoot.querySelector('[data-mm-variant]').id}`;
      this.editButton.setAttribute('aria-controls', this.options.id);
      this.editButton.setAttribute('aria-expanded', 'true');
      this.controls = {};
      const labelFor = selector => this.form.querySelector(selector)?.textContent.trim();
      const fields = [
        ['variant', labelFor('.mm-variant-choices legend') || labelFor('.control-label') || 'Choose your option', 'select'],
        ['mode', labelFor('.purchase-options legend') || 'Purchase option', 'select'],
        ['quantity', this.dataset.quantityLabel, 'input'],
        ['plan', labelFor('[for^="mm-plan-"]') || 'Delivery frequency', 'select']
      ];
      fields.forEach(([key, text, tag]) => {
        const label = document.createElement('label');
        const caption = document.createElement('span');
        caption.textContent = text;
        const control = document.createElement(tag);
        control.dataset.mmDockControl = key;
        if (key === 'quantity') { control.type = 'number'; control.inputMode = 'numeric'; }
        label.append(caption, control);
        this.controls[key] = control;
        this.options.append(label);
        control.addEventListener(key === 'quantity' ? 'input' : 'change', () => {
          const source = key === 'variant' ? this.form.querySelector('[data-mm-variant]')
            : key === 'plan' ? this.form.querySelector('[data-mm-plan]')
            : key === 'quantity' ? this.form.querySelector('[name="quantity"]')
            : this.form.querySelector(`[name="mm_mode"][value="${control.value}"]`);
          if (!source || source.disabled || this.form.getAttribute('aria-busy') === 'true') return;
          if (key === 'mode') source.checked = true;
          else source.value = control.value;
          source.dispatchEvent(new Event('change', { bubbles: true }));
          this.update();
        }, { signal });
      });
      this.querySelector('.mm-purchase-dock__inner').insertBefore(this.options, this.querySelector('.mm-purchase-dock__price'));
    }

    toggleOptions() {
      this.options.hidden = !this.options.hidden;
      this.editButton.setAttribute('aria-expanded', String(!this.options.hidden));
    }

    syncOptions() {
      let data;
      try { data = JSON.parse(this.sourceData.textContent); } catch { return; }
      const variantSource = this.form.querySelector('[data-mm-variant]');
      const variant = data.variants.find(item => String(item.id) === variantSource.value);
      if (!variant) return;
      const modeSource = this.form.querySelector('[name="mm_mode"]:checked');
      const mode = modeSource?.value || 'once';
      const updateSelect = (control, choices, selected) => {
        const signature = JSON.stringify(choices);
        if (control.dataset.options !== signature) {
          control.replaceChildren(...choices.map(choice => {
            const option = document.createElement('option');
            option.value = choice.value; option.textContent = choice.label; option.disabled = Boolean(choice.disabled);
            return option;
          }));
          control.dataset.options = signature;
        }
        control.value = selected;
      };
      updateSelect(this.controls.variant, data.variants.map(item => ({
        value: String(item.id), label: item.title.replace(/^(Starter Duo|Larger Duo) — /, ''), disabled: variantSource.querySelector(`option[value="${item.id}"]`)?.disabled ?? true
      })), variantSource.value);
      const modeChoices = [];
      if (!variant.requiresPlan) modeChoices.push({ value: 'once', label: this.dataset.onceLabel });
      if (variant.plans.length) modeChoices.push({ value: 'subscribe', label: this.dataset.subscribeLabel });
      updateSelect(this.controls.mode, modeChoices, mode);
      this.controls.mode.closest('label').hidden = modeChoices.length < 2;
      const planSource = this.form.querySelector('[data-mm-plan]');
      updateSelect(this.controls.plan, variant.plans.map(plan => ({
        value: String(plan.id), label: `${plan.name} · ${this.formatMoney.format(plan.price / 100)}`
      })), planSource.value);
      this.controls.plan.closest('label').hidden = mode !== 'subscribe' || variant.plans.length < 2;
      const quantitySource = this.form.querySelector('[name="quantity"]');
      if (document.activeElement !== this.controls.quantity) this.controls.quantity.value = quantitySource.value;
      ['min', 'max', 'step'].forEach(attribute => {
        if (quantitySource.hasAttribute(attribute)) this.controls.quantity.setAttribute(attribute, quantitySource.getAttribute(attribute));
        else this.controls.quantity.removeAttribute(attribute);
      });
      const busy = this.form.getAttribute('aria-busy') === 'true';
      Object.values(this.controls).forEach(control => { control.disabled = busy; });

    }

    selection() {
      let data;
      try { data = JSON.parse(this.sourceData.textContent); } catch { return null; }
      if (!Array.isArray(data?.variants)) return null;
      const variantControl = this.form.querySelector('[data-mm-variant]');
      const variant = data.variants.find(item => String(item.id) === variantControl?.value);
      const quantityControl = this.form.querySelector('[name="quantity"]');
      const quantity = Number(quantityControl?.value);
      const planControl = this.form.querySelector('[data-mm-plan]');
      const modeControl = this.form.querySelector('[name="mm_mode"]:checked');
      const subscription = modeControl?.value === 'subscribe';
      const plan = subscription && !planControl?.disabled
        ? variant?.plans?.find(item => String(item.id) === planControl?.value)
        : null;
      if (!variant || !Number.isInteger(quantity) || quantity < 1 || !quantityControl.validity.valid) return null;
      if (!['once', 'subscribe'].includes(modeControl?.value)) return null;
      if (subscription && (!plan || planControl.disabled)) return null;
      if (variant.requiresPlan && !subscription) return null;
      const price = Number(subscription ? plan.price : variant.price);
      if (!Number.isSafeInteger(price) || price < 0 || !Number.isSafeInteger(price * quantity)) return null;
      return { variant, quantity, subscription, plan, price, total: price * quantity };
    }

    update() {
      if (!this.isConnected || !this.form.isConnected) {
        this.hidden = true;
        return;
      }
      this.syncOptions();
      const state = this.selection();
      if (!state) {
        this.addButton.disabled = true;
        this.buyButton.disabled = true;
        this.summary.textContent = '';
        this.total.textContent = '';
        this.unit.textContent = '';
        this.unit.hidden = true;
        this.reveal();
        return;
      }
      this.buyButton.textContent = this.sourceBuy.textContent;
      const mode = state.subscription ? this.dataset.subscribeLabel : this.dataset.onceLabel;
      const pack = state.variant.title ? `${state.variant.title.replace(/^(Starter Duo|Larger Duo) — /, '')} × ${state.quantity}` : `${this.dataset.quantityLabel} ${state.quantity}`;
      this.summary.textContent = [pack, mode].filter(Boolean).join(' · ');
      this.editButton.title = this.summary.textContent;
      this.total.textContent = this.formatMoney.format(state.total / 100);
      const unitSource = this.productRoot.querySelector('[data-mm-selected-unit-price]');
      this.unit.textContent = unitSource?.textContent.trim() || '';
      this.unit.hidden = !this.unit.textContent;
      const image = this.querySelector('.mm-purchase-dock__image');
      const sourceImage = this.productRoot.querySelector('.pdp-image');
      const sourceUrl = state.variant.image || sourceImage?.currentSrc || sourceImage?.src;
      if (image && sourceUrl && image.getAttribute('src') !== sourceUrl) {
        image.src = sourceUrl;
        image.removeAttribute('srcset');
      }
      const busy = this.form.getAttribute('aria-busy') === 'true';
      this.addButton.disabled = busy || this.sourceAdd.disabled || this.sourceAdd.getAttribute('aria-disabled') === 'true';
      this.buyButton.disabled = busy || this.sourceBuy.disabled || this.sourceBuy.getAttribute('aria-disabled') === 'true';
      this.setAttribute('aria-busy', String(busy));
      this.reveal(state);
    }

    topOffset() {
      return [...document.querySelectorAll('[data-mm-sticky-header], .header, .mm-dispatch-bar')]
        .filter(element => ['fixed', 'sticky'].includes(getComputedStyle(element).position))
        .map(element => element.getBoundingClientRect())
        .sort((first, second) => first.top - second.top)
        .reduce((offset, rect) => {
          return rect.top <= offset + 1 && rect.bottom > 0 ? Math.max(offset, rect.bottom) : offset;
        }, 0);
    }

    reveal(state) {
      const offset = this.topOffset();
      this.style.setProperty('--mm-purchase-dock-top', `${offset}px`);
      const actions = this.sourceActions.getBoundingClientRect();
      const footer = document.querySelector('[data-mm-site-footer]') || document.querySelector('footer, .footer');
      const footerRect = footer?.getBoundingClientRect();
      const height = window.visualViewport?.height || window.innerHeight;
      const footerVisible = footerRect && footerRect.height > 0 && footerRect.top < height && footerRect.bottom > 0;
      const overlay = document.querySelector('dialog[open], .mm-menu[open], .mm-shop-desktop[open]');
      const blocked = overlay || document.body.classList.contains('mm-gel-consent-open');
      const focus = document.activeElement;
      const keyboard = height < window.innerHeight * 0.75 && focus?.matches('input, textarea, select, [contenteditable="true"]');
      const passed = actions.height > 0 && actions.bottom <= offset;
      this.hidden = !passed || Boolean(footerVisible || blocked || keyboard);
    }

    edit() {
      this.hidden = true;
      const offset = this.topOffset() + 16;
      const top = this.form.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top: Math.max(0, top), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      const target = this.form.querySelector('[data-mm-variant-choice]:checked') || this.form.querySelector('[name="quantity"]');
      target?.focus({ preventScroll: true });
    }

    submit(button) {
      this.update();
      const state = this.selection();
      if (!state || !button || button.form !== this.form || button.disabled || button.getAttribute('aria-disabled') === 'true' || this.form.getAttribute('aria-busy') === 'true') return;
      if (!this.form.checkValidity()) {
        this.edit();
        this.form.reportValidity();
        return;
      }
      this.form.requestSubmit(button);
    }
  }

  customElements.define('mm-purchase-dock', MilliesPurchaseDock);
})();
